import { transcribeRecording } from "/js/transcription.js";
import { logError } from "~vue/utils/logUtils";
import { type Ref, ref } from "vue";

export interface AudioStats {
    audiblePercentage: number;
    avgLevel: number;
    peakLevel: number;
    warmupMs: number;
}

interface UseTranscriptionOptions {
    source: string;
    error?: Ref<string | null>;
    errorMessage?: string;
}

export const useTranscription = ({ source, error = ref(null), errorMessage = "Something went wrong. Please try again." }: UseTranscriptionOptions) => {
    const isTranscribing = ref(false);

    const transcribe = async ({ blob, audioStats }: { blob: Blob; audioStats?: AudioStats }) => {
        const errorContext = {
            context: {
                transcription: {
                    source,
                    blob_size: blob?.size,
                    blob_type: blob?.type,
                    ...(audioStats && {
                        audible_pct: audioStats.audiblePercentage,
                        avg_level: audioStats.avgLevel,
                        peak_level: audioStats.peakLevel,
                        warmup_ms: audioStats.warmupMs,
                    }),
                },
            },
            tags: { error_type: "transcription" },
        };

        try {
            error.value = null;
            const transcript = await transcribeRecording({
                blob,
                onStart: () => {
                    isTranscribing.value = true;
                },
                onError: (err: { error_code?: string }) => {
                    error.value = errorMessage;
                    logError(err, {
                        ...errorContext,
                        tags: { ...errorContext.tags, ...(err.error_code && { error_code: err.error_code }) },
                    });
                },
            });
            isTranscribing.value = false;
            return transcript as string;
        } catch (e) {
            error.value = errorMessage;
            logError(e, errorContext);
            isTranscribing.value = false;
            return null;
        }
    };

    return { isTranscribing, transcribe };
};
